'use client'

import {
  DisplayTokenFragment,
  OobDisplayTokenFragment,
  ProjectDisplayFragment,
  Render_Types_Enum
} from '@/gql/generated/graphql'
import * as React from 'react'
import { DetailText, VideoDisplay } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { motion, AnimatePresence } from 'motion/react'
import useMeasure from 'react-use-measure'
import { LoadingSpinner } from '@components/loading-spinner'
import { cn } from '@/lib/utils'

export type TokenDisplayProps = {
  token: DisplayTokenFragment | OobDisplayTokenFragment
  project: ProjectDisplayFragment
  preferredDisplayType?: Render_Types_Enum
  onClick?: () => void
  autoPlay?: boolean
  loadingScreen?: boolean
  /**
   * If true, the iframe content will only reload when the browser window is resized.
   * If false (default), it will reload whenever the component's bounding box changes,
   * which can happen during parent layout animations (e.g., animating padding).
   * Set this to true if the component is inside a container that might animate its size,
   * and you want to avoid unnecessary iframe reloads during those animations.
   */
  reloadOnWindowResizeOnly?: boolean
}

export const TokenDisplay = ({
  token,
  project,
  preferredDisplayType,
  onClick,
  autoPlay,
  loadingScreen = true,
  reloadOnWindowResizeOnly = false
}: TokenDisplayProps): React.ReactElement | null => {
  let displayType = preferredDisplayType ?? project.primary_render_type
  if (displayType === Render_Types_Enum.Video && !token?.video_id) {
    displayType = Render_Types_Enum.Image
  }

  const [opacity, setOpacity] = React.useState(0)
  const [loaded, setLoaded] = React.useState(false)
  const [showLoadingScreen, setShowLoadingScreen] = React.useState(false)

  // How long to wait before showing the loading screen
  const LOADING_WAIT_TIME = 1500

  React.useEffect(() => {
    setOpacity(0)
    setLoaded(false)
    setShowLoadingScreen(false)
  }, [token.token_id])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (!loaded) {
        setShowLoadingScreen(true)
      }
    }, LOADING_WAIT_TIME)
    return () => clearTimeout(timer) // Clean up the timeout on component unmount
  }, [loaded, token.token_id, displayType])

  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const [frameRef, frameBounds] = useMeasure()

  // Reload the iframe when the window is resized
  React.useEffect(() => {
    if (reloadOnWindowResizeOnly) {
      return
    }

    if (iframeRef.current && token.live_view_url && frameBounds.width && frameBounds.height) {
      iframeRef.current.src = token.live_view_url
    }
  }, [frameBounds.width, frameBounds.height, token.live_view_url, reloadOnWindowResizeOnly])

  React.useEffect(() => {
    if (!reloadOnWindowResizeOnly) {
      return
    }

    const handleResize = () => {
      if (iframeRef.current && token.live_view_url) {
        iframeRef.current.src = token.live_view_url
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [reloadOnWindowResizeOnly, token.live_view_url])

  // Clean up iframe when switching to non-live view
  React.useEffect(() => {
    if (displayType !== Render_Types_Enum.Live && iframeRef.current) {
      iframeRef.current.src = ''
    }
  }, [displayType])

  let displayComponent
  const hasOnClick = !!onClick

  if (token && token.media_url && (displayType === Render_Types_Enum.Image || displayType === Render_Types_Enum.Gif)) {
    displayComponent = (
      <motion.button
        key={`${token?.token_id}-${displayType}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: opacity }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.35,
          ease: 'easeIn'
        }}
        onClick={(event) => {
          event.stopPropagation()
          onClick?.()
        }}
        className={cn('relative h-full w-full', hasOnClick ? 'cursor-pointer' : 'cursor-default')}
      >
        <ImageWithCustomLoader
          src={token.media_url}
          style={{
            objectFit: 'contain',
            objectPosition: 'center'
          }}
          className="shadow-artwork h-full w-full"
          fill
          priority
          alt={`${project.name} #${token.token_id}`}
          sizes="100vw"
          onLoad={() => setOpacity(1)}
          blurHash={token.image?.metadata?.blurHash}
        />
      </motion.button>
    )
  } else if (displayType === Render_Types_Enum.Video && token.video_id) {
    const videoUrl = token.media_url?.replace('png', 'mp4') ?? ''
    displayComponent = (
      <div className="relative h-full w-full">
        <VideoDisplay
          key={`${token?.token_id}-${displayType}`}
          src={videoUrl}
          placeholderSrc={token.media_url ?? ''}
          blurHash={token.image?.metadata?.blurHash}
          onClick={(event) => {
            event.stopPropagation()
            onClick?.()
          }}
          autoPlay={autoPlay}
        />
      </div>
    )
  } else {
    displayComponent = (
      <>
        <AnimatePresence>
          {!loaded && loadingScreen && showLoadingScreen ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.5,
                ease: 'easeIn'
              }}
            >
              <div className="gap-y-xs absolute inset-0 z-[70] flex h-full w-full flex-col items-center justify-center">
                <LoadingSpinner className="w-sm h-sm" light />
                <DetailText className="text-on-surface-inverted mt-xs font-medium">RENDERING ARTWORK</DetailText>
              </div>

              <motion.div initial={{ opacity: 0.1 }}>
                <ImageWithCustomLoader
                  src={token.media_url ?? ''}
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                  fill
                  priority
                  alt={`${project.name} #${token.token_id}`}
                  sizes="90vw"
                  blurHash={token.image?.metadata?.blurHash}
                />
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
        <motion.iframe
          title={`${project.name} #${token.token_id} live view`}
          key={`${token?.token_id}-${displayType}`}
          ref={iframeRef}
          // layout="position"

          initial={{ opacity: 0 }}
          animate={{ opacity: opacity }}
          exit={{ opacity: 0 }}
          transition={{
            delay: 0.1,
            duration: 0.4,
            ease: 'easeInOut'
          }}
          // eslint-disable-next-line
          // @ts-ignore
          allowvr="yes"
          allowFullScreen
          // Security considerations for iframe permissions:
          // - allow-scripts: Required to run js scripts
          // - allow-downloads: Enables file downloads from iframe content (ex: script allows saving an image)
          // - camera/xr-spatial-tracking/accelerometer/gyroscope: Device access permissions for interactive art
          // - autoplay: Enables automatic media playback
          // - encrypted-media: Enables DRM capabilities for protected content
          // - allowFullScreen: Enables fullscreen mode
          allow="camera; xr-spatial-tracking; accelerometer; autoplay; encrypted-media; gyroscope;"
          sandbox="allow-scripts allow-downloads"
          scrolling="no" // even though this is deprecated, please don't delete - only way to disable scroll bars
          src={token?.live_view_url && frameBounds.width && frameBounds.height ? token.live_view_url : ''}
          className={`relative h-full w-full overflow-hidden`}
          onMouseEnter={() => {
            // Focus on hover as a backup method for regaining focus
            iframeRef.current?.focus()
            try {
              iframeRef.current?.contentWindow?.focus()
            } catch (error) {
              console.debug('Could not focus iframe content window on hover:', error)
            }
          }}
          onLoad={() => {
            setOpacity(1)
            setLoaded(true)
            setShowLoadingScreen(false)
          }}
        />
      </>
    )
  }

  return (
    <motion.div className={`relative h-full w-full`} ref={frameRef}>
      <div
        className="absolute inset-0 m-auto max-h-full max-w-full"
        style={{
          aspectRatio: project.aspect_ratio
        }}
      >
        {displayComponent}
      </div>
    </motion.div>
  )
}
